import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LiliButton } from '@lili/lili-frontend-common';
import { ReactComponent as EyeClosed } from '../../../assets/icons/svg/shared/ic-shared-eye-closed.svg';
import { ReactComponent as EyeOpened } from '../../../assets/icons/svg/shared/ic-shared-eye-opened.svg';
import ContentWrapper from '../../../generic-components/ContentWrapper';
import MainContent from '../../../generic-components/Layout/MainContent';
import { useMyLiliCardQuery } from '../../../hooks/query/useMyLiliCardQuery';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { useStore } from '../../../store';
import Icon from '../../../generic-components/Icon';
import RightArrow from '../../../assets/icons/svg/shared/ic-shared-right-arrow.svg';
import LiliCardFrozen from '../../../assets/png/card-frozen.png';
import LiliCreditCard from '../../../assets/png/lili-card.png';
import {
  cardActionsBrokenCardIcon,
  cardActionsChangePinIcon,
  cardActionsCreditCardIcon,
  cardActionsFreezeCard,
  frozenCard,
} from '../../../assets/svg';
import somethingWentWrong from '../../../assets/svg/something-went-wrong.svg';
import { FEATURE_FLAG } from '../../../constants/general';
import Heading from '../../../generic-components/Heading';
import SubContent from '../../../generic-components/Layout/SubContent';
import ExpandIcon from '../../../assets/icons/svg/shared/ic-shared-expand.svg';
import CardCashback from '../../../assets/icons/svg/large-icons/ic-large-card-cashback.svg';
import {
  canActivatePhysicalCard,
  canCashbackRewards,
  canFreezeCard,
  canSetPin,
  getMostRelevantCard,
} from '../../../helpers/cardStatus.helper';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';
import { useFreezeCardQuery } from '../../../hooks/query/useFreezeCardQuery';
import { validateFeatureFlag } from '../../../services/featureFlagService';
import InfoPopup from '../../dashboard/components/InfoPopup';
import { POPUP_LIFESPAN } from '../../emergencyBucketDashboard/constants';
import { LOGIN_ERROR_POPUP } from '../../login/constants';
import UpperMessagePopUp from '../../transactions/components/UpperMessagePopUp';
import CardActionButton from '../components/cardActionButton';
import {
  // COMING_SOON_MODAL_TEXTS,
  ERROR_MODAL_TEXTS,
  LILI_CARD_CARD_ACTIONS,
  LILI_CARD_MESSAGE,
  LILI_CARD_STYLES,
  MAX_BUTTON_PRESSES,
  VSE_LINK,
} from '../constants';
import { reportActOnSpeedBump, reportViewSpeedBump } from '../../../analytics/speedBump.analytics';
import { ActionModal } from '../../../DesignSystem/ActionModal';
import PageLayout from '../../../generic-components/PageLayout/PageLayout';
import { BillingAddress } from './BillingAddress';

const LiliCard = () => {
  const navigate = useNavigate();
  const unlockMessage = 'FREEZE_CARD_POPUP_UNLOCK_MESSAGE';
  const lockMessage = 'FREEZE_CARD_POPUP_LOCK_MESSAGE';
  const { getPersonalAccount } = useStore((state) => state);
  const [detailsIsShown, setDetailsIsShown] = useState<boolean>(false);
  const [showUpperMessage, setShowUpperMessage] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isCardFreezing, setIsCardFreezing] = useState<boolean>(false);
  const [showCardButtonPressedCount, setShowCardButtonPressedCount] = useState<number>(1);
  const [cardImage, setCardImage] = useState<string>('');
  const [isCardLocked, setIsCardLocked] = useState<boolean>();
  const [isFreezePopupShown, setIsFreezePopupShown] = useState<boolean>(false);
  // const [isComingSoonPopupShown, setIsComingSoonPopupShown] = useState<boolean>(false);
  const [showLeaveWarning, setShowLeaveWarning] = useState<boolean>(false);
  const [thereIsErrorPopupShown, setThereIsErrorPopupShown] = useState<boolean>(false);
  const [popupMessage, setPopupMessage] = useState<typeof lockMessage | typeof unlockMessage>(lockMessage);
  const [showVSE, setShowVSE] = useState<boolean>(false);
  const mostSignificantCard = getMostRelevantCard(getPersonalAccount().cards);
  const [cardActionsState, setCardActionsState] = useState({
    numOfActionsAvailable: 0,
    canActivatePhysicalCard: canActivatePhysicalCard(mostSignificantCard),
    canFreezeCard: canFreezeCard(mostSignificantCard),
    canSetPin: canSetPin(mostSignificantCard),
    canCashbackRewards: canCashbackRewards(mostSignificantCard),
  });
  const { refetch: updateSummary } = useAccountSummaryQuery(() => {});
  const { data, refetch, isSuccess, isLoading } = useMyLiliCardQuery(() => {
    setDetailsIsShown(true);
    setLoading(false);
    setThereIsErrorPopupShown(true);
  });

  const { refetch: freezeCard } = useFreezeCardQuery({
    freeze: !isCardLocked,
    onSuccess: () => {
      setDetailsIsShown(false);
      setPopupMessage(!isCardLocked ? lockMessage : unlockMessage);
      setIsCardLocked(!isCardLocked);
      setIsCardFreezing(false);
      setIsFreezePopupShown(false);
      updateSummary().then(() => {
        setShowUpperMessage(true);
        setTimeout(() => {
          setShowUpperMessage(false);
        }, POPUP_LIFESPAN);
      });
    },
    onError: () => {
      setLoading(false);
      setThereIsErrorPopupShown(true);
      setDetailsIsShown(false);
      setIsFreezePopupShown(false);
      setIsCardFreezing(false);
    },
  });

  const shouldShowVSE = () => {
    if (validateFeatureFlag(FEATURE_FLAG.VSE)) {
      setShowVSE(true);
    }
  };

  const isSetPinFlagEnabled = validateFeatureFlag(FEATURE_FLAG.SET_PIN);

  const handleShowSpeedBump = () => {
    setShowLeaveWarning(true);
    reportViewSpeedBump('Visa SavingsEdge');
  };

  const handleRedirectOutside = () => {
    reportActOnSpeedBump('Continue');
    trackMixPanel('See All VSE Offers');
    window.open(VSE_LINK, '_blank');
    setShowLeaveWarning(false);
  };

  const handleCancelRedirectOutside = () => {
    reportActOnSpeedBump('Back');
    setShowLeaveWarning(false);
  };

  const handleShowCardDetailsClick = async () => {
    setLoading(true);
    trackMixPanel('Show Card Details');

    if (showCardButtonPressedCount === MAX_BUTTON_PRESSES) {
      await refetch();
      setShowCardButtonPressedCount(0);
      setCardImage(isCardLocked ? LiliCardFrozen : LiliCreditCard);
    } else if (!detailsIsShown && !data) {
      await refetch();
    } else if (!detailsIsShown && data) {
      setShowCardButtonPressedCount((prevState) => prevState + 1);
      setCardImage(data);
    } else if (detailsIsShown) {
      setCardImage(isCardLocked ? LiliCardFrozen : LiliCreditCard);
    }
    setDetailsIsShown((prevState) => !prevState);
  };

  const handleFreezeCard = async () => {
    if (showCardButtonPressedCount === MAX_BUTTON_PRESSES) {
      await refetch();
      setShowCardButtonPressedCount(0);
    }
    if (isCardLocked) {
      trackMixPanel('Unfreeze card', {
        Origin: 'Card settings',
      });
      setIsCardFreezing(true);
      await freezeCard();
    } else {
      trackMixPanel('Freeze card', {
        Origin: 'Card settings',
      });
      setIsCardFreezing(false);
      setIsFreezePopupShown(true);
    }
  };

  useEffect(() => {
    setCardImage(data);
  }, [isSuccess]);

  useEffect(() => {
    trackMixPanel('Select View my card');
    shouldShowVSE();

    if (mostSignificantCard?.cardId && mostSignificantCard?.locked !== isCardLocked) {
      setIsCardLocked(mostSignificantCard.locked);
    }
    // Check for number of card actions available for this user, and store this result in numOfActionsAvailable
    if (mostSignificantCard) {
      let i = 0;
      Object.entries(cardActionsState).forEach(([, value]) => {
        if (value) i++;
      });
      if (i > 1) {
        setCardActionsState((prevState) => ({ ...prevState, numOfActionsAvailable: i }));
      }
    }
  }, []);

  useEffect(() => {
    setCardImage(!detailsIsShown && isCardLocked ? LiliCardFrozen : LiliCreditCard);
  }, [isCardLocked]);

  const { t } = useTranslation();

  return (
    <PageLayout>
      <ContentWrapper styles='flex gap-6 justify-center'>
        <MainContent className='flex flex-col gap-4'>
          <Heading
            title={t('MY_LILI_CARD_PAGE_HEADER')}
            className='font-metricRSemibold text-bw08 text-[20px] mb-[24px]'
          />
          <div className={LILI_CARD_STYLES.MAIN_CONTENT}>
            <div className={LILI_CARD_STYLES.CARD_IMAGE_CONTAINER}>
              <img
                onLoad={() => {
                  setLoading(false);
                }}
                id='liliCardImage'
                src={cardImage}
                alt='myLiliCard'
                className={LILI_CARD_STYLES.CARD_IMAGE}
              />
            </div>
            <div className={LILI_CARD_STYLES.TEXT_CONTAINER}>
              <div>
                <div className={LILI_CARD_STYLES.TEXT_STYLES}>
                  <span data-testid='MY_LILI_CARD_TITLE'>{t(LILI_CARD_MESSAGE.FIRST_LINE)}</span>
                </div>
              </div>
              <LiliButton
                id='showOrHideLiliCard'
                disabled={loading || isLoading}
                className='h-[54px] w-[224px]'
                isLoading={loading || isLoading}
                onClick={handleShowCardDetailsClick}
                icon={
                  detailsIsShown ? (
                    <EyeClosed className='text-bw00 h-[28px] w-[28px]' />
                  ) : (
                    <EyeOpened className='text-bw00 h-[28px] w-[28px]' />
                  )
                }
                title={t(detailsIsShown ? LILI_CARD_MESSAGE.BUTTON_HIDE_TEXT : LILI_CARD_MESSAGE.BUTTON_SHOW_TEXT)}
              />
            </div>
          </div>
          {showVSE && (
            <div className='flex flex-col bg-bw01 rounded-xl w-[684px] pc-md:w-[744px] h-[300px] py-[24px] pl-[27px] pr-[32px]'>
              <div className='flex text-base text-bw08 font-metricMedium'>
                <span data-testid='VSE_HEADER'>{t('VSE_HEADER')}</span>
              </div>
              <div className='flex-row w-full'>
                <img
                  src={CardCashback}
                  className='flex basis-[100px] float-right w-[100px] h-[100px] tablet:hidden pc-md:flex'
                  alt='cashback'
                />
                <div
                  data-testid='VSE_TEXT'
                  className='flex font-metric text-bw07 text-[14px] w-[530px] h-[48px] mt-2 mb-[12px]'
                >
                  {t('VSE_TEXT')}
                </div>
                <div
                  data-testid='VSE_TEXT_SECOND_LINE'
                  className='font-metric text-bw07 text-[14px] w-[530px] h-[48px] mb-6'
                >
                  {t('VSE_TEXT_SECOND_LINE')}
                </div>
              </div>
              <div className='flex gap-16'>
                <LiliButton
                  data-testId='VSE_ALL_OFFERS_BUTTON'
                  onClick={handleShowSpeedBump}
                  title={t('VSE_ALL_OFFERS_BUTTON_TEXT')}
                  className='w-[232px] h-[54px]'
                  id='SeeAllVseOffers'
                  suffixIcon={<Icon iconId='expand' src={ExpandIcon} />}
                />
              </div>
              {showLeaveWarning && (
                <ActionModal
                  title={t('NAVIGATE_OUTSIDE_MODAL.TITLE')}
                  description={t('NAVIGATE_OUTSIDE_MODAL.DESCRIPTION')}
                  onSubmit={handleRedirectOutside}
                  onCancel={handleCancelRedirectOutside}
                  cancelButtonTitle={t('NAVIGATE_OUTSIDE_MODAL.CANCEL')}
                  mainButtonTitle={t('NAVIGATE_OUTSIDE_MODAL.CONTINUE')}
                  disclaimerText={t('NAVIGATE_OUTSIDE_MODAL.DISCLAIMER')}
                />
              )}
              <div data-testid='VSE_DISCLAIMER' className='font-metric text-bw06 text-xxs w-[673px] h-[27px] mt-[16px]'>
                {t('VSE_DISCLAIMER')}
              </div>
            </div>
          )}
        </MainContent>
        <SubContent
          className='flex flex-col bg-bw00 rounded-xl w-[280px] pc-md:w-[360px] mt-[70px] pt-[40px] px-[20px] pc-md:px-[40px]'
        >
          <span data-testid='CARD_ACTIONS_TITLE' className='h-[25px] text-base font-metricMedium text-bw08 mb-[15px]'>
            {t(cardActionsState.numOfActionsAvailable > 1 ? 'CARD_ACTIONS' : 'CARD_ACTION')}
          </span>
          <div className='w-[240px] pc-md:w-[280px] border-b border-b-[0.5px] border-bw06' />
          {cardActionsState.canActivatePhysicalCard && isSetPinFlagEnabled && (
            <CardActionButton
              buttonTitle={t(LILI_CARD_CARD_ACTIONS.ACTIVATE_CARD)}
              leftIcon={cardActionsCreditCardIcon}
              rightIcon={RightArrow}
              actionId='ActivatePhysicalCard'
              onClick={() => navigate('/activate-card')}
            />
          )}
          {cardActionsState.canFreezeCard && (
            <CardActionButton
              buttonTitle={t(LILI_CARD_CARD_ACTIONS.CARD_FREEZE)}
              leftIcon={cardActionsFreezeCard}
              toggleState={isCardLocked}
              handleToggle={async () => {
                await handleFreezeCard();
              }}
              actionId='FreezeCard'
            />
          )}
          {cardActionsState.canSetPin && isSetPinFlagEnabled && (
            <CardActionButton
              buttonTitle={t(LILI_CARD_CARD_ACTIONS.CHANGE_PIN)}
              leftIcon={cardActionsChangePinIcon}
              rightIcon={RightArrow}
              actionId='ChangePin'
              onClick={() => navigate('/change-pin')}
            />
          )}
          {cardActionsState.canCashbackRewards && (
            <CardActionButton
              buttonTitle={t(LILI_CARD_CARD_ACTIONS.ORDER_NEW_CARD)}
              leftIcon={cardActionsBrokenCardIcon}
              rightIcon={RightArrow}
              actionId='OrderNewCard'
              onClick={() => navigate('/order-new-card')}
            />
          )}
          <BillingAddress />
          {/* <div className='flex justify-center mt-auto'>
            <CommonHeading.H10
              fontWeight={FontWeight.SEMIBOLD}
              title='Billing Address'
              className='text-center text-text-main-primary'
            >
              Billing Address
            </CommonHeading.H10>
          </div> */}
        </SubContent>
      </ContentWrapper>
      {isFreezePopupShown && (
        <InfoPopup
          isLoading={isCardFreezing}
          popupHeight='h-[485px]'
          handleState={() => {}}
          pictureClassName='w-[90px] h-[67px] mb-[3.75rem]'
          pictureSrc={frozenCard}
          showInfoPopup={isFreezePopupShown}
          text={t('FREEZE_CARD_POPUP_TITLE')}
          title={t('FREEZE_CARD_POPUP_TEXT')}
          titleClassName='text-center px-20 text-base font-metricMedium text-bw08 mb-[1rem]'
          mainButtonText={t('FREEZE_CARD_POPUP_YES_BTN')}
          secondaryHandleState={() => {
            setIsFreezePopupShown(false);
          }}
          secondaryButtonText={isCardFreezing ? '' : t('FREEZE_CARD_POPUP_NO_BTN')}
          secondaryButtonClassName='mt-4 text-sm text-bw06 font-metricMedium hover:text-action03'
          additionalCallback={async () => {
            setIsCardFreezing(true);
            await freezeCard();
          }}
          textClassName='mb-[60px]'
          buttonClassName='w-[240px] h-[50px]'
        />
      )}
      {/* {isComingSoonPopupShown && ( */}
      {/* <InfoPopup */}
      {/*  popupHeight='h-[485px] pb-8' */}
      {/*  handleState={() => { }} */}
      {/*  pictureClassName='w-[90px] h-[60px] mt-[92px] mb-[50px]' */}
      {/*  pictureSrc={cardActionsComingSoonIcon} */}
      {/*  showInfoPopup={isComingSoonPopupShown} */}
      {/*  text={t(COMING_SOON_MODAL_TEXTS.TEXT)} */}
      {/*  textClassName='px-[34px]' */}
      {/*  title={t(COMING_SOON_MODAL_TEXTS.TITLE)} */}
      {/*  titleClassName='text-center px-20 text-base font-metricMedium text-bw08 mb-[1rem]' */}
      {/*  buttonClassName='min-h-[50px] w-[240px] mt-8' */}
      {/*  mainButtonText={t(COMING_SOON_MODAL_TEXTS.BTN)} */}
      {/*  secondaryHandleState={() => { setIsComingSoonPopupShown(false); }} */}
      {/*  additionalCallback={() => setIsComingSoonPopupShown(false)} */}
      {/* /> */}
      {/* )} */}
      {thereIsErrorPopupShown && (
        <InfoPopup
          popupHeight='h-[485px]'
          handleState={() => {}}
          pictureClassName='w-[122px] h-[108px] mt-5 mb-[40px]'
          pictureSrc={somethingWentWrong}
          showInfoPopup={thereIsErrorPopupShown}
          text={t(LOGIN_ERROR_POPUP.ERROR_TEXT)}
          textClassName='px-[34px] whitespace-pre-line mb-8'
          title={t(LOGIN_ERROR_POPUP.HEADER)}
          titleClassName='text-center px-20 text-base font-metricMedium text-bw08 mb-[1rem]'
          buttonClassName='min-h-[50px] w-[240px]'
          mainButtonText={t(ERROR_MODAL_TEXTS.BTN)}
          additionalCallback={() => setThereIsErrorPopupShown(false)}
        />
      )}
      {showUpperMessage && (
        <UpperMessagePopUp
          handleClose={() => {
            setShowUpperMessage(false);
          }}
          messageText={t(popupMessage)}
        />
      )}
    </PageLayout>
  );
};

export default LiliCard;
